import { getContractQuantityTerms, NEW_CONTRACT_QUANTITY_TERMS } from "@anthemreactv2/contracts";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCargosWithDetails } from "@/hooks/useCargos";
import { useSuggestedContractPrices } from "@/hooks/useContracts";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import {
	buildCustomerComboboxOptions,
	getCustomerDisplayLabel,
} from "@/lib/customer-combobox";
import { formatCurrency } from "@/lib/format";
import { hasPermission } from "@/lib/roles";

type ContractItem = {
	id: string;
	cargoId: string;
	totalBags: number;
	pricePerLb: number;
	pricingMode: "auto" | "manual";
};

type ReleaseAllocation = {
	itemId: string;
	bags: number;
};

type ReleaseRow = {
	id: string;
	startDate: string;
	notes: string;
	allocations: ReleaseAllocation[];
};

const RELEASE_WINDOW_DAYS = 15;
const RELEASE_GRACE_PERIOD_DAYS = 5;
const MAXIMUM_CONTRACT_WINDOW_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_CONTRACT_TYPE = "release_schedule";
const DEFAULT_PAYMENT_TERMS = "prepay_before_release";

function createContractItem(): ContractItem {
	return {
		id: crypto.randomUUID(),
		cargoId: "",
		totalBags: 0,
		pricePerLb: 0,
		pricingMode: "auto",
	};
}

function createReleaseRow(itemIds: string[]): ReleaseRow {
	return {
		id: crypto.randomUUID(),
		startDate: "",
		notes: "",
		allocations: itemIds.map((itemId) => ({ itemId, bags: 0 })),
	};
}

function synchronizeReleaseRows(rows: ReleaseRow[], itemIds: string[]): ReleaseRow[] {
	return rows.map((row) => {
		const existingAllocations = new Map(
			row.allocations.map((allocation) => [allocation.itemId, allocation]),
		);

		return {
			...row,
			allocations: itemIds.map(
				(itemId) => existingAllocations.get(itemId) ?? { itemId, bags: 0 },
			),
		};
	});
}

function parseIsoDateToTimestamp(value: string): number | null {
	if (!value) return null;
	const timestamp = Date.parse(`${value}T00:00:00`);
	return Number.isFinite(timestamp) ? timestamp : null;
}

function parseWholeNumber(value: string): number {
	const parsed = Number.parseInt(value, 10);
	if (!Number.isFinite(parsed) || parsed < 0) return 0;
	return parsed;
}

function parseNonNegativeFloat(value: string): number {
	const parsed = Number.parseFloat(value);
	if (!Number.isFinite(parsed) || parsed < 0) return 0;
	return parsed;
}

function getBagWeightLb(cargo: any): number | null {
	const configuredWeight = Number(cargo?.product?.weight);
	if (!Number.isFinite(configuredWeight) || configuredWeight <= 0) {
		return null;
	}
	return configuredWeight;
}

function calculateReleaseWindowDates(startDate: number) {
	const releaseDeadline =
		startDate + Math.max(0, RELEASE_WINDOW_DAYS - 1) * MS_PER_DAY;
	const gracePeriodEnds = releaseDeadline + RELEASE_GRACE_PERIOD_DAYS * MS_PER_DAY;
	return {
		releaseDeadline,
		gracePeriodEnds,
	};
}

function calculateMaximumContractEndDate(startDate: number): number {
	return startDate + Math.max(0, MAXIMUM_CONTRACT_WINDOW_DAYS - 1) * MS_PER_DAY;
}

function calculateLatestReleaseStartDate(endDate: number): number {
	return endDate - (Math.max(0, RELEASE_WINDOW_DAYS - 1) + RELEASE_GRACE_PERIOD_DAYS) * MS_PER_DAY;
}

function formatTimestampAsInputDate(timestamp: number | null): string | undefined {
	if (timestamp == null) return undefined;
	const date = new Date(timestamp);
	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	const dd = String(date.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

function formatPreviewDate(timestamp: number | null): string {
	if (timestamp == null) return "-";
	return new Date(timestamp).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

export default function SalesContractsNewPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { userRole } = useAuthIdentity();
	const users = useUsersForImpersonation();
	const cargos = useCargosWithDetails(1000, false);
	const createMutation = useMutation(api.contractWorkflow.adminCreateDraftContract);
	const updateMutation = useMutation(api.contractWorkflow.adminUpdateDraftContract);
	const draftId = searchParams.get("draftId");
	const isEditingDraft = Boolean(draftId);
	const draftContract = useQuery(
		api.contractWorkflow.getContractDetails,
		draftId ? { contractId: draftId as any } : "skip",
	);
	const canCreateContracts = hasPermission(userRole, "contracts:approve");
	const quantityTerms = draftContract ? getContractQuantityTerms(draftContract) : NEW_CONTRACT_QUANTITY_TERMS;
	const releaseMinimumBags = quantityTerms.releaseMinimumBags;
	const releaseIncrementBags = quantityTerms.releaseQuantityIncrementBags;

	const [customerId, setCustomerId] = React.useState("");
	const [startDate, setStartDate] = React.useState("");
	const [endDate, setEndDate] = React.useState("");
	const [deliveryTerms, setDeliveryTerms] = React.useState(
		"Warehouse handling included; LTL freight billed per release",
	);
	const [notes, setNotes] = React.useState("");
	const [items, setItems] = React.useState<ContractItem[]>([createContractItem()]);
	const [releaseRows, setReleaseRows] = React.useState<ReleaseRow[]>([
		createReleaseRow([]),
	]);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const hydratedDraftIdRef = React.useRef<string | null>(null);

	React.useEffect(() => {
		setReleaseRows((current) => synchronizeReleaseRows(current, items.map((item) => item.id)));
	}, [items]);

	React.useEffect(() => {
		if (!draftId || !draftContract) return;
		if (hydratedDraftIdRef.current === draftId) return;
		if ((draftContract.status || draftContract.contractStatus) !== "draft") return;

		const nextItems: ContractItem[] =
			Array.isArray(draftContract.items) && draftContract.items.length > 0
				? draftContract.items.map((item: any) => ({
					id: String(item._id ?? crypto.randomUUID()),
					cargoId: String(item.cargoId ?? ""),
					totalBags: Number(item.totalBags ?? item.quantity ?? 0),
					pricePerLb: Number(item.unitPrice ?? 0),
					pricingMode: "manual" as const,
				}))
				: [createContractItem()];
		const nextItemIds = nextItems.map((item) => item.id);
		const nextReleaseRows =
			Array.isArray(draftContract.releaseRows) && draftContract.releaseRows.length > 0
				? synchronizeReleaseRows(
						draftContract.releaseRows.map((row: any) => ({
							id: String(row._id ?? crypto.randomUUID()),
							startDate: formatTimestampAsInputDate(row.clientSelectedStartDate) ?? "",
							notes: row.notes ?? "",
							allocations: Array.isArray(row.allocations)
								? row.allocations.map((allocation: any) => ({
									itemId: String(allocation.contractItemId),
									bags: Number(allocation.plannedBags ?? 0),
								}))
								: [],
						})),
						nextItemIds,
				  )
				: [createReleaseRow(nextItemIds)];

		setCustomerId(String(draftContract.userId ?? ""));
		setStartDate(formatTimestampAsInputDate(draftContract.startDate) ?? "");
		setEndDate(formatTimestampAsInputDate(draftContract.endDate) ?? "");
		setDeliveryTerms(
			draftContract.deliveryTerms ||
				"Warehouse handling included; LTL freight billed per release",
		);
		setNotes(draftContract.notes ?? draftContract.adminNotes ?? "");
		setItems(nextItems);
		setReleaseRows(nextReleaseRows);
		hydratedDraftIdRef.current = draftId;
	}, [draftContract, draftId]);
	const selectedCustomer = React.useMemo(
		() => users?.find((u: any) => u._id === customerId) ?? null,
		[users, customerId],
	);

	const customerOptions = React.useMemo(() => {
		return buildCustomerComboboxOptions(users as any[] | undefined);
	}, [users]);

	const activeCargos = React.useMemo<any[]>(
		() =>
			Array.isArray(cargos)
				? cargos.filter((c: any) => c.status !== "archived" && c.status !== "sold_out")
				: [],
		[cargos],
	);

	const cargoById = React.useMemo<Map<string, any>>(() => {
		return new Map(activeCargos.map((cargo: any) => [cargo._id, cargo]));
	}, [activeCargos]);

	const selectedCargoIds = React.useMemo(
		() => items.map((item) => item.cargoId).filter(Boolean),
		[items],
	);
	const suggestedPrices = useSuggestedContractPrices(customerId || undefined, selectedCargoIds);
	const suggestedPriceByCargoId = React.useMemo(() => {
		const map = new Map<string, number>();
		for (const suggestion of suggestedPrices ?? []) {
			const suggestedPrice = Number(suggestion?.suggestedPrice);
			if (suggestion?.cargoId && Number.isFinite(suggestedPrice) && suggestedPrice > 0) {
				map.set(String(suggestion.cargoId), suggestedPrice);
			}
		}
		return map;
	}, [suggestedPrices]);

	const availabilityByCargoId = React.useMemo(() => {
		const map = new Map<string, { available: number; reserved: number; label: string }>();

		for (const cargo of activeCargos) {
			const availableBags = Math.max(0, Number(cargo.availableBags ?? 0));
			const reservedBags = Math.max(0, Number(cargo.reservedBags ?? 0));
			const actuallyAvailable = Math.max(0, availableBags - reservedBags);
			const label = cargo.ociNumber || cargo.product?.name || cargo._id;

			map.set(cargo._id, {
				available: actuallyAvailable,
				reserved: reservedBags,
				label,
			});
		}

		return map;
	}, [activeCargos]);

	const requestedBagsByCargoId = React.useMemo(() => {
		const map = new Map<string, number>();
		for (const item of items) {
			if (!item.cargoId || item.totalBags <= 0) continue;
			map.set(item.cargoId, (map.get(item.cargoId) ?? 0) + item.totalBags);
		}
		return map;
	}, [items]);

	const cargoOptions = React.useMemo(() => {
		return activeCargos.map((cargo: any) => {
			const available = Math.max(
				0,
				Number(cargo.availableBags ?? 0) - Number(cargo.reservedBags ?? 0),
			);
			const originName = String(cargo.product?.origin?.name ?? cargo.product?.origin ?? "").trim();
			const label = `${cargo.ociNumber || "No OCI"} - ${cargo.product?.name || "Unknown Product"}`;
			const sublabel = [originName, `${available} available`].filter(Boolean).join(" · ");
			const searchText = [cargo.ociNumber, cargo.product?.name, originName]
				.filter(Boolean)
				.join(" ");

			return {
				value: cargo._id,
				label,
				sublabel: sublabel || undefined,
				searchText: searchText || undefined,
			};
		});
	}, [activeCargos]);

	const startDateTimestamp = React.useMemo(
		() => parseIsoDateToTimestamp(startDate),
		[startDate],
	);
	const endDateTimestamp = React.useMemo(
		() => parseIsoDateToTimestamp(endDate),
		[endDate],
	);
	const maximumContractEndDate = React.useMemo(
		() =>
			startDateTimestamp != null
				? calculateMaximumContractEndDate(startDateTimestamp)
				: null,
		[startDateTimestamp],
	);
	const latestReleaseStartDate = React.useMemo(
		() =>
			endDateTimestamp != null
				? calculateLatestReleaseStartDate(endDateTimestamp)
				: null,
		[endDateTimestamp],
	);
	const dateRangeError =
		startDateTimestamp != null &&
		endDateTimestamp != null &&
		startDateTimestamp > endDateTimestamp
			? "End date must be on or after start date."
			: null;
	const contractWindowError =
		startDateTimestamp != null &&
		endDateTimestamp != null &&
		maximumContractEndDate != null &&
		endDateTimestamp > maximumContractEndDate
			? `Contract end date must stay within ${MAXIMUM_CONTRACT_WINDOW_DAYS} days of the start date.`
			: null;

	React.useEffect(() => {
		if (!customerId) {
			setItems((current) => {
				let changed = false;
				const next = current.map((item) => {
					if (item.pricingMode !== "auto" || item.pricePerLb === 0) {
						return item;
					}
					changed = true;
					return { ...item, pricePerLb: 0 };
				});
				return changed ? next : current;
			});
			return;
		}

		if (!suggestedPrices) {
			return;
		}

		setItems((current) => {
			let changed = false;
			const next = current.map((item) => {
				if (!item.cargoId || item.pricingMode !== "auto") {
					return item;
				}
				const suggestedPrice = suggestedPriceByCargoId.get(item.cargoId) ?? 0;
				if (item.pricePerLb === suggestedPrice) {
					return item;
				}
				changed = true;
				return { ...item, pricePerLb: suggestedPrice };
			});
			return changed ? next : current;
		});
	}, [customerId, suggestedPriceByCargoId, suggestedPrices]);

	const itemMetaById = React.useMemo(() => {
		const map = new Map<string, { productName: string; bagWeightLb: number | null; totalBags: number }>();
		for (const item of items) {
			const cargo = cargoById.get(item.cargoId);
			map.set(item.id, {
				productName: cargo?.product?.name || cargo?.ociNumber || `Item ${item.id.slice(0, 4)}`,
				bagWeightLb: getBagWeightLb(cargo),
				totalBags: item.totalBags,
			});
		}
		return map;
	}, [cargoById, items]);

	const availabilityIssues = React.useMemo(() => {
		const issues: string[] = [];
		const seenCargoIds = new Set<string>();
		for (const item of items) {
			if (!item.cargoId) continue;
			if (seenCargoIds.has(item.cargoId)) {
				issues.push("Each contract item must use a different cargo.");
				continue;
			}
			seenCargoIds.add(item.cargoId);
			const availability = availabilityByCargoId.get(item.cargoId);
			if (!availability) {
				issues.push(`Selected cargo ${item.cargoId} is not available.`);
				continue;
			}
			if (item.totalBags > availability.available) {
				issues.push(
					`${availability.label}: requested ${item.totalBags} bags, only ${availability.available} available.`,
				);
			}
			if (item.totalBags > 0 && getBagWeightLb(cargoById.get(item.cargoId)) == null) {
				issues.push(`${availability.label}: bag weight is missing on the selected cargo product.`);
			}
		}
		return issues;
	}, [availabilityByCargoId, cargoById, items]);

	const totalBags = React.useMemo(
		() => items.reduce((sum, item) => sum + Math.max(0, item.totalBags), 0),
		[items],
	);

	const totalPounds = React.useMemo(() => {
		return items.reduce((sum, item) => {
			if (!item.cargoId || item.totalBags <= 0) return sum;
			const bagWeight = getBagWeightLb(cargoById.get(item.cargoId));
			if (bagWeight == null) return sum;
			return sum + item.totalBags * bagWeight;
		}, 0);
	}, [cargoById, items]);

	const totalValue = React.useMemo(() => {
		return items.reduce((sum, item) => {
			if (item.totalBags <= 0 || item.pricePerLb <= 0) return sum;
			const bagWeight = getBagWeightLb(cargoById.get(item.cargoId));
			if (bagWeight == null) return sum;
			return sum + item.totalBags * bagWeight * item.pricePerLb;
		}, 0);
	}, [cargoById, items]);

	const addItem = React.useCallback(() => {
		setItems((current) => [...current, createContractItem()]);
	}, []);

	const removeItem = React.useCallback((itemId: string) => {
		setItems((current) => current.filter((item) => item.id !== itemId));
	}, []);

	const updateItem = React.useCallback(
		(itemId: string, updates: Partial<ContractItem>) => {
			setItems((current) =>
				current.map((item) => (item.id === itemId ? { ...item, ...updates } : item)),
			);
		},
		[],
	);

	const handleCargoChange = React.useCallback(
		(itemId: string, cargoId: string) => {
			const suggestedPrice = customerId ? suggestedPriceByCargoId.get(cargoId) ?? 0 : 0;
			updateItem(itemId, {
				cargoId,
				pricePerLb: suggestedPrice,
				pricingMode: "auto",
			});
		},
		[customerId, suggestedPriceByCargoId, updateItem],
	);

	const handlePriceChange = React.useCallback(
		(itemId: string, rawValue: string) => {
			updateItem(itemId, {
				pricePerLb: parseNonNegativeFloat(rawValue),
				pricingMode: "manual",
			});
		},
		[updateItem],
	);

	const handleBagsChange = React.useCallback(
		(itemId: string, rawValue: string) => {
			const parsedValue = parseWholeNumber(rawValue);
			const currentItem = items.find((item) => item.id === itemId);
			if (!currentItem || !currentItem.cargoId) {
				updateItem(itemId, { totalBags: parsedValue });
				return;
			}

			const requestedForCargo = requestedBagsByCargoId.get(currentItem.cargoId) ?? 0;
			const otherRowsRequested = Math.max(0, requestedForCargo - currentItem.totalBags);
			const availableForCargo = availabilityByCargoId.get(currentItem.cargoId)?.available ?? 0;
			const maxForCurrentRow = Math.max(0, availableForCargo - otherRowsRequested);

			updateItem(itemId, { totalBags: Math.min(parsedValue, maxForCurrentRow) });
		},
		[availabilityByCargoId, items, requestedBagsByCargoId, updateItem],
	);

	const addReleaseRow = React.useCallback(() => {
		setReleaseRows((current) => [...current, createReleaseRow(items.map((item) => item.id))]);
	}, [items]);

	const removeReleaseRow = React.useCallback((rowId: string) => {
		setReleaseRows((current) => current.filter((row) => row.id !== rowId));
	}, []);

	const updateReleaseRow = React.useCallback(
		(rowId: string, field: keyof Pick<ReleaseRow, "startDate" | "notes">, value: string) => {
			setReleaseRows((current) =>
				current.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
			);
		},
		[],
	);

	const updateReleaseAllocation = React.useCallback(
		(rowId: string, itemId: string, rawValue: string) => {
			const parsedValue = parseWholeNumber(rawValue);
			setReleaseRows((current) =>
				current.map((row) => {
					if (row.id !== rowId) return row;
					return {
						...row,
						allocations: row.allocations.map((allocation) =>
							allocation.itemId === itemId
								? { ...allocation, bags: parsedValue }
								: allocation,
						),
					};
				}),
			);
		},
		[],
	);

	const preparedReleaseRows = React.useMemo(() => {
		return releaseRows.map((row, index) => {
			const startDateTimestamp = parseIsoDateToTimestamp(row.startDate);
			const windowDates =
				startDateTimestamp != null
					? calculateReleaseWindowDates(startDateTimestamp)
					: null;

			return {
				...row,
				releaseNumber: index + 1,
				startDateTimestamp,
				releaseDeadline: windowDates?.releaseDeadline ?? null,
				gracePeriodEnds: windowDates?.gracePeriodEnds ?? null,
				rowTotalBags: row.allocations.reduce((sum, allocation) => sum + allocation.bags, 0),
			};
		});
	}, [releaseRows]);

	const scheduledBagsByItemId = React.useMemo(() => {
		const map = new Map<string, number>();
		for (const row of releaseRows) {
			for (const allocation of row.allocations) {
				if (allocation.bags <= 0) continue;
				map.set(allocation.itemId, (map.get(allocation.itemId) ?? 0) + allocation.bags);
			}
		}
		return map;
	}, [releaseRows]);

	const releasePlanIssues = React.useMemo(() => {
		const issues: string[] = [];
		let previousGracePeriodEnds: number | null = null;

		for (const row of preparedReleaseRows) {
			if (row.startDateTimestamp == null) {
				issues.push(`Release ${row.releaseNumber} needs a start date.`);
				continue;
			}
			if (startDateTimestamp != null && row.startDateTimestamp < startDateTimestamp) {
				issues.push(`Release ${row.releaseNumber} cannot start before the contract start date.`);
			}
			if (endDateTimestamp != null && row.gracePeriodEnds != null && row.gracePeriodEnds > endDateTimestamp) {
				issues.push(`Release ${row.releaseNumber} exceeds the contract end date.`);
			}
			if (
				previousGracePeriodEnds != null &&
				row.startDateTimestamp <= previousGracePeriodEnds
			) {
				issues.push(`Release ${row.releaseNumber} overlaps the previous release window.`);
			}

			const nonZeroAllocations = row.allocations.filter((allocation) => allocation.bags > 0);
			if (nonZeroAllocations.length === 0) {
				issues.push(`Release ${row.releaseNumber} must include at least one planned allocation.`);
			}

			for (const allocation of nonZeroAllocations) {
				if (allocation.bags < releaseMinimumBags) {
					const itemLabel = itemMetaById.get(allocation.itemId)?.productName || "Item";
					issues.push(`${itemLabel} in Release ${row.releaseNumber} must be at least ${releaseMinimumBags} bags.`);
				}
				if (allocation.bags % releaseIncrementBags !== 0) {
					const itemLabel = itemMetaById.get(allocation.itemId)?.productName || "Item";
					issues.push(`${itemLabel} in Release ${row.releaseNumber} must be in multiples of ${releaseIncrementBags} bags.`);
				}
			}

			if (row.gracePeriodEnds != null) {
				previousGracePeriodEnds = row.gracePeriodEnds;
			}
		}

		for (const item of items) {
			if (!item.cargoId || item.totalBags <= 0) continue;
			const scheduledBags = scheduledBagsByItemId.get(item.id) ?? 0;
			if (scheduledBags !== item.totalBags) {
				const itemLabel = itemMetaById.get(item.id)?.productName || "Item";
				issues.push(
					`${itemLabel} must be fully scheduled. Planned ${scheduledBags} of ${item.totalBags} bags.`,
				);
			}
		}

		return Array.from(new Set(issues));
	}, [endDateTimestamp, itemMetaById, items, preparedReleaseRows, scheduledBagsByItemId, startDateTimestamp, releaseMinimumBags, releaseIncrementBags]);

	const hasValidLineItem = items.some(
		(item) => item.cargoId && item.totalBags > 0 && item.pricePerLb > 0,
	);
	const hasInvalidSelectedLine = items.some(
		(item) =>
			item.cargoId &&
			(item.totalBags <= 0 ||
				item.pricePerLb <= 0 ||
				item.totalBags < releaseMinimumBags ||
				item.totalBags % releaseIncrementBags !== 0),
	);

	const canSubmit = Boolean(
		customerId &&
			startDateTimestamp != null &&
			endDateTimestamp != null &&
			!dateRangeError &&
			!contractWindowError &&
			hasValidLineItem &&
			!hasInvalidSelectedLine &&
			availabilityIssues.length === 0 &&
			releasePlanIssues.length === 0,
	);

	const handleSubmit = React.useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (dateRangeError) {
				toast.error(dateRangeError);
				return;
			}
			if (contractWindowError) {
				toast.error(contractWindowError);
				return;
			}
			if (availabilityIssues.length > 0) {
				toast.error(availabilityIssues[0]);
				return;
			}
			if (releasePlanIssues.length > 0) {
				toast.error(releasePlanIssues[0]);
				return;
			}
			if (!canSubmit || startDateTimestamp == null || endDateTimestamp == null) return;

			setIsSubmitting(true);
			try {
				const validItems = items.filter(
					(item) => item.cargoId && item.totalBags > 0 && item.pricePerLb > 0,
				);
				if (validItems.length === 0) {
					toast.error("Add at least one valid contract item before saving.");
					return;
				}

				const itemIndexById = new Map(
					validItems.map((item, index) => [item.id, index]),
				);

				const preparedItems = validItems.map((item) => ({
					cargoId: item.cargoId as any,
					totalBags: item.totalBags,
					unitPrice: item.pricePerLb,
					priceUnit: "lb",
				}));

				const preparedRows = preparedReleaseRows.map((row) => ({
					clientSelectedStartDate: row.startDateTimestamp!,
					notes: row.notes.trim() || undefined,
					allocations: row.allocations
						.filter((allocation) => allocation.bags > 0 && itemIndexById.has(allocation.itemId))
						.map((allocation) => ({
							itemIndex: itemIndexById.get(allocation.itemId)!,
							plannedBags: allocation.bags,
						})),
				}));

				if (isEditingDraft && draftId) {
					await updateMutation({
						contractId: draftId as any,
						updates: {
							startDate: startDateTimestamp,
							endDate: endDateTimestamp,
							contractType: DEFAULT_CONTRACT_TYPE,
							paymentTerms: DEFAULT_PAYMENT_TERMS,
							deliveryTerms: deliveryTerms || undefined,
							adminNotes: notes || undefined,
							agreementTemplateVersion: "green_coffee_purchase_agreement",
						},
						items: preparedItems,
						releaseRows: preparedRows,
					});
					toast.success("Draft contract updated");
					navigate(`/sales/contracts/${draftId}`);
				} else {
					const result = await createMutation({
						userId: customerId as any,
						startDate: startDateTimestamp,
						endDate: endDateTimestamp,
						contractType: DEFAULT_CONTRACT_TYPE,
						paymentTerms: DEFAULT_PAYMENT_TERMS,
						deliveryTerms: deliveryTerms || undefined,
						adminNotes: notes || undefined,
						agreementTemplateVersion: "green_coffee_purchase_agreement",
						items: preparedItems,
						releaseRows: preparedRows,
					});

					toast.success("Draft contract created");
					navigate(result?.contractId ? `/sales/contracts/${result.contractId}` : "/sales/contracts");
				}
			} catch (err) {
				toast.error(
					err instanceof Error
						? err.message
						: isEditingDraft
							? "Failed to update contract"
							: "Failed to create contract",
				);
			} finally {
				setIsSubmitting(false);
			}
		},
		[
			availabilityIssues,
			canSubmit,
			contractWindowError,
			createMutation,
			customerId,
			dateRangeError,
			draftId,
			deliveryTerms,
			endDateTimestamp,
			items,
			isEditingDraft,
			navigate,
			notes,
			preparedReleaseRows,
			releasePlanIssues,
			startDateTimestamp,
			updateMutation,
		],
	);

	if (!canCreateContracts) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Contract creation is admin-only</CardTitle>
					<CardDescription>Only admins can create or edit contracts.</CardDescription>
				</CardHeader>
				<CardContent>
					<Button asChild variant="outline">
						<Link to="/sales/contracts">Back to Contracts</Link>
					</Button>
				</CardContent>
			</Card>
		);
	}

	if (isEditingDraft && draftContract === undefined) {
		return (
			<Card>
				<CardContent className="py-10 text-center text-muted-foreground">
					Loading draft contract...
				</CardContent>
			</Card>
		);
	}

	if (isEditingDraft && !draftContract) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Draft Contract Not Found</CardTitle>
					<CardDescription>The requested draft contract could not be loaded.</CardDescription>
				</CardHeader>
				<CardContent>
					<Button asChild variant="outline">
						<Link to="/sales/contracts">Back to Contracts</Link>
					</Button>
				</CardContent>
			</Card>
		);
	}

	if (isEditingDraft && (draftContract.status || draftContract.contractStatus) !== "draft") {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Only Draft Contracts Can Be Edited</CardTitle>
					<CardDescription>Submitted or approved contracts must be managed from the detail page.</CardDescription>
				</CardHeader>
				<CardContent>
					<Button asChild variant="outline">
						<Link to={draftId ? `/sales/contracts/${draftId}` : "/sales/contracts"}>Back</Link>
					</Button>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
			<Card>
				<CardHeader>
					<CardTitle>{isEditingDraft ? "Edit Draft Contract" : "Create Contract"}</CardTitle>
					<CardDescription>
						{isEditingDraft
							? "Update the draft contract and release schedule before submitting it for approval."
							: "Create a new draft contract with item-level release scheduling."}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form className="space-y-6" onSubmit={(e) => void handleSubmit(e)}>
						<div className="space-y-2">
							<Label>Customer</Label>
							<ComboboxSelect
								options={customerOptions}
								value={customerId}
								onValueChange={setCustomerId}
								disabled={isEditingDraft}
								placeholder="Search and select a customer..."
								searchPlaceholder="Search by company, name, or email..."
								emptyText="No customers found."
								loading={users === undefined}
							/>
						</div>

						<div className="grid gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<Label>Start Date</Label>
								<DatePicker
									value={startDate}
									onChange={setStartDate}
									placeholder="Select start date"
									maxDate={endDate || undefined}
								/>
							</div>
							<div className="space-y-2">
								<Label>End Date</Label>
								<DatePicker
									value={endDate}
									onChange={setEndDate}
									placeholder="Select end date"
									minDate={startDate || undefined}
									maxDate={formatTimestampAsInputDate(maximumContractEndDate)}
								/>
							</div>
						</div>
						{dateRangeError ? (
							<p className="text-xs font-medium text-red-600">{dateRangeError}</p>
						) : null}
						{contractWindowError ? (
							<p className="text-xs font-medium text-red-600">{contractWindowError}</p>
						) : null}
						{maximumContractEndDate != null ? (
							<p className="text-xs text-muted-foreground">
								Maximum allowed end date: {formatPreviewDate(maximumContractEndDate)}
							</p>
						) : null}

						<div className="space-y-2">
							<Label>Delivery Terms</Label>
							<Input
								value={deliveryTerms}
								onChange={(e) => setDeliveryTerms(e.target.value)}
								placeholder="e.g., Warehouse handling included; freight billed per release"
							/>
						</div>

						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<Label>Contract Items</Label>
								<Button type="button" variant="outline" size="sm" onClick={addItem}>
									+ Add Item
								</Button>
							</div>
							{items.map((item) => {
								const cargo = item.cargoId ? cargoById.get(item.cargoId) : null;
								const availability = item.cargoId
									? availabilityByCargoId.get(item.cargoId)
									: null;
								const requestedForCargo = item.cargoId
									? requestedBagsByCargoId.get(item.cargoId) ?? 0
									: 0;
								const remainingForCargo = availability
									? availability.available - requestedForCargo
									: 0;
								const bagWeightLb = getBagWeightLb(cargo);
								const suggestedPrice = item.cargoId
									? suggestedPriceByCargoId.get(item.cargoId) ?? null
									: null;
								const isLoadingSuggestedPrice =
									Boolean(customerId && item.cargoId) && suggestedPrices === undefined;

								return (
									<div key={item.id} className="space-y-2 rounded border p-3">
										<div className="grid gap-3 lg:grid-cols-[1.4fr_120px_120px_auto]">
											<div className="space-y-1">
												<Label className="text-xs">Cargo</Label>
												<ComboboxSelect
													options={cargoOptions}
													value={item.cargoId}
													onValueChange={(value) => handleCargoChange(item.id, value)}
													placeholder="Search and select cargo..."
													searchPlaceholder="Search by OCI, product, or origin..."
													emptyText="No cargos found."
													loading={cargos === undefined}
												/>
											</div>
											<div className="space-y-1">
												<Label className="text-xs">Total Bags</Label>
												<Input
													type="number"
													min={0}
													step={releaseIncrementBags}
													value={item.totalBags || ""}
													onChange={(e) => handleBagsChange(item.id, e.target.value)}
												/>
											</div>
											<div className="space-y-1">
												<Label className="text-xs">$/lb</Label>
												<Input
													type="number"
													step="0.01"
													min={0}
													value={item.pricePerLb || ""}
													onChange={(e) => handlePriceChange(item.id, e.target.value)}
												/>
												{customerId && item.cargoId ? (
													<p className="text-xs text-muted-foreground">
														{isLoadingSuggestedPrice
															? "Loading tier + market price…"
															: suggestedPrice != null
																? item.pricingMode === "manual"
																	? `Suggested ${formatCurrency(suggestedPrice)}/lb from tier + market price`
																	: "Auto-filled from customer tier + market price"
																: "No suggested tier/market price found for this cargo"}
													</p>
												) : null}
											</div>
											<div className="flex items-end justify-end">
												{items.length > 1 ? (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => removeItem(item.id)}
													>
														Remove
													</Button>
												) : null}
											</div>
										</div>

										<div className="flex flex-wrap gap-1">
											{availability ? (
												<Badge variant="outline">{availability.available} available</Badge>
											) : null}
											{availability?.reserved ? (
												<Badge variant="outline">{availability.reserved} reserved</Badge>
											) : null}
											{item.totalBags > 0 ? (
												<Badge variant="outline">{item.totalBags} requested</Badge>
											) : null}
											{item.cargoId ? (
												<Badge
													variant="outline"
													className={
														remainingForCargo < 0
															? "border-red-300 text-red-700 dark:border-red-800/60 dark:text-red-300"
															: "border-emerald-300 text-emerald-700 dark:border-emerald-800/60 dark:text-emerald-300"
													}
												>
													{remainingForCargo < 0
														? `Over by ${Math.abs(remainingForCargo)}`
														: `${remainingForCargo} remaining`}
												</Badge>
											) : null}
											{bagWeightLb != null ? (
												<Badge variant="outline">{bagWeightLb.toFixed(2)} lb bag weight</Badge>
											) : item.cargoId ? (
												<Badge variant="outline" className="border-red-300 text-red-700 dark:border-red-800/60 dark:text-red-300">
													Missing bag weight
												</Badge>
											) : null}
											{customerId && item.cargoId && suggestedPrice != null ? (
												<Badge variant="outline">Suggested {formatCurrency(suggestedPrice)}/lb</Badge>
											) : null}
										</div>

										{item.totalBags > 0 && item.totalBags < releaseMinimumBags ? (
											<p className="text-xs font-medium text-red-600">
												Each item must include at least {releaseMinimumBags} bags.
											</p>
										) : null}
										{item.totalBags > 0 && item.totalBags % releaseIncrementBags !== 0 ? (
											<p className="text-xs font-medium text-red-600">
												Each item must use multiples of {releaseIncrementBags} bags.
											</p>
										) : null}
									</div>
								);
							})}

							{availabilityIssues.length > 0 ? (
								<div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800/60 dark:bg-red-950/30">
									<p className="text-xs font-semibold text-red-700 dark:text-red-300">
										Fix inventory and item limits before saving:
									</p>
									<ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-300">
										{availabilityIssues.map((issue) => (
											<li key={issue}>{issue}</li>
										))}
									</ul>
								</div>
							) : null}
						</div>

						<div className="space-y-3 rounded-lg border p-4">
							<div className="flex items-center justify-between">
								<div>
									<Label>Release Schedule</Label>
									<p className="text-xs text-muted-foreground">
										Plan item quantities by release. Each non-zero allocation must include at least {releaseMinimumBags} bags, in increments of {releaseIncrementBags}.
									</p>
								</div>
								<Button type="button" variant="outline" size="sm" onClick={addReleaseRow}>
									+ Add Release
								</Button>
							</div>

							{preparedReleaseRows.map((row, rowIndex) => {
								const previousRelease = rowIndex > 0 ? preparedReleaseRows[rowIndex - 1] : null;
								const previousReleaseGraceEnds = previousRelease?.gracePeriodEnds ?? null;
								const releaseMinTimestamp =
									previousReleaseGraceEnds != null
										? Math.max(
												startDateTimestamp ?? previousReleaseGraceEnds + MS_PER_DAY,
												previousReleaseGraceEnds + MS_PER_DAY,
										  )
										: startDateTimestamp;
								const releaseDateDisabled =
									startDateTimestamp == null ||
									endDateTimestamp == null ||
									(rowIndex > 0 && previousReleaseGraceEnds == null);
								const releaseDatePlaceholder =
									startDateTimestamp == null || endDateTimestamp == null
										? "Select contract dates first"
										: rowIndex > 0 && previousReleaseGraceEnds == null
											? "Select previous release date first"
											: "Select release date";

								return (
								<div key={row.id} className="space-y-3 rounded border p-3">
									<div className="flex flex-wrap items-start justify-between gap-3">
										<div>
											<p className="font-medium">Release {row.releaseNumber}</p>
											<p className="text-xs text-muted-foreground">
												Deadline {formatPreviewDate(row.releaseDeadline)} · Grace Ends {formatPreviewDate(row.gracePeriodEnds)}
											</p>
										</div>
										<div className="flex items-center gap-2">
											<div className="min-w-[220px] space-y-1">
												<Label className="text-xs">Client Start Date</Label>
												<DatePicker
													value={row.startDate}
													onChange={(value) => updateReleaseRow(row.id, "startDate", value)}
													placeholder={releaseDatePlaceholder}
													disabled={releaseDateDisabled}
													minDate={formatTimestampAsInputDate(releaseMinTimestamp)}
													maxDate={formatTimestampAsInputDate(latestReleaseStartDate)}
												/>
											</div>
											{preparedReleaseRows.length > 1 ? (
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => removeReleaseRow(row.id)}
												>
													Remove
												</Button>
											) : null}
										</div>
									</div>

									<div className="space-y-2">
										{items.map((item) => {
											const allocation = row.allocations.find(
												(entry) => entry.itemId === item.id,
											);
											const meta = itemMetaById.get(item.id);

											return (
												<div key={`${row.id}-${item.id}`} className="grid gap-2 md:grid-cols-[minmax(0,1fr)_120px] md:items-end">
													<div>
														<p className="text-xs font-medium">
															{meta?.productName || "Select an item first"}
														</p>
														<p className="text-xs text-muted-foreground">
															Contract total {item.totalBags || 0} bags · Scheduled {(scheduledBagsByItemId.get(item.id) ?? 0)} bags
														</p>
													</div>
													<div className="space-y-1">
														<Label className="text-xs">Planned Bags</Label>
														<Input
															type="number"
															min={0}
															step={releaseIncrementBags}
															value={allocation?.bags || ""}
															onChange={(e) =>
																updateReleaseAllocation(row.id, item.id, e.target.value)
															}
															disabled={!item.cargoId}
														/>
													</div>
												</div>
											);
										})}
									</div>

									<div className="grid gap-3 md:grid-cols-2">
										<div className="space-y-1">
											<Label className="text-xs">Release Notes</Label>
											<Textarea
												value={row.notes}
												onChange={(e) => updateReleaseRow(row.id, "notes", e.target.value)}
												className="min-h-[72px]"
											/>
										</div>
										<div className="space-y-1">
											<Label className="text-xs">Release Summary</Label>
											<div className="rounded border px-3 py-2 text-sm">
												<p>Total scheduled this release: {row.rowTotalBags} bags</p>
												<p>Deadline: {formatPreviewDate(row.releaseDeadline)}</p>
												<p>Grace ends: {formatPreviewDate(row.gracePeriodEnds)}</p>
											</div>
										</div>
									</div>
								</div>
								);
							})}

							{releasePlanIssues.length > 0 ? (
								<div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800/60 dark:bg-red-950/30">
									<p className="text-xs font-semibold text-red-700 dark:text-red-300">
										Fix release schedule issues before saving:
									</p>
									<ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-300">
										{releasePlanIssues.map((issue) => (
											<li key={issue}>{issue}</li>
										))}
									</ul>
								</div>
							) : null}
						</div>

						<div className="space-y-2">
							<Label>Internal Notes</Label>
							<Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
						</div>

						<div className="flex gap-2">
							<Button type="submit" disabled={!canSubmit || isSubmitting}>
								{isSubmitting ? (isEditingDraft ? "Saving..." : "Creating...") : isEditingDraft ? "Save Changes" : "Save Draft"}
							</Button>
							<Button asChild type="button" variant="ghost">
								<Link to="/sales/contracts">Back</Link>
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Draft Preview</CardTitle>
					<CardDescription>
						Live summary of the contract program you are building.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4 text-sm">
					<div className="space-y-1">
						<p>
							<span className="font-medium">Customer:</span>{" "}
							{getCustomerDisplayLabel(selectedCustomer) || "—"}
						</p>
						<p>
							<span className="font-medium">Term:</span> {startDate || "—"} → {endDate || "—"}
						</p>
					</div>

					<div className="space-y-1 rounded border p-3">
						<p>
							<span className="font-medium">Total Bags:</span> {totalBags}
						</p>
						<p>
							<span className="font-medium">Total Weight:</span>{" "}
							{totalPounds > 0 ? `${totalPounds.toLocaleString()} lb` : "—"}
						</p>
						<p>
							<span className="font-medium">Est. Total Value:</span> {formatCurrency(totalValue)}
						</p>
					</div>

					<div className="space-y-2">
						<p className="font-medium">Release Rows</p>
						{preparedReleaseRows.map((row) => (
							<div key={row.id} className="rounded border p-3">
								<p className="font-medium">Release {row.releaseNumber}</p>
								<p className="text-xs text-muted-foreground">
									Start {row.startDate || "—"} · Deadline {formatPreviewDate(row.releaseDeadline)} · Grace Ends {formatPreviewDate(row.gracePeriodEnds)}
								</p>
								<ul className="mt-2 space-y-1 text-xs text-muted-foreground">
									{row.allocations
										.filter((allocation) => allocation.bags > 0)
										.map((allocation) => (
											<li key={`${row.id}-${allocation.itemId}`}>
												{itemMetaById.get(allocation.itemId)?.productName || "Item"}: {allocation.bags} bags
											</li>
										))}
									{row.allocations.every((allocation) => allocation.bags <= 0) ? (
										<li>No planned quantities yet.</li>
									) : null}
								</ul>
							</div>
						))}
					</div>

					<p className="text-xs text-muted-foreground">
						This draft will snapshot the agreement text, set Day 15 / Day 20 windows automatically, and require every item to be fully scheduled before save.
					</p>
				</CardContent>
			</Card>
		</div>
	);
}
